/**
 * Real Terrain Analysis API Integration
 * Fetches comprehensive terrain analysis with NO MOCK DATA
 * All parameters calculated from real elevation and weather data
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5005';

/**
 * Perform comprehensive real terrain analysis for a route
 * @param {object} route - Route data with coordinates, distance, etc.
 * @param {object} elevationData - Pre-fetched elevation data (optional, will fetch if not provided)
 * @param {object} bbox - Bounding box for elevation data (only used if elevationData not provided)
 * @param {object} vehicleData - Vehicle data (cargo weight, component health)
 * @returns {Promise<object>} Complete real terrain analysis
 */
export const analyzeRouteReal = async (route, elevationData = null, bbox = null, vehicleData = null) => {
    try {
        console.log('🔬 Requesting REAL route terrain analysis...');
        
        // Only send essential route data to reduce payload size
        const essentialRouteData = {
            coordinates: route.coordinates,
            distance: route.distance,
            duration: route.duration,
            ascent: route.ascent,
            descent: route.descent
        };
        
        // Prepare request body
        const requestBody = {
            route: essentialRouteData,
            vehicleData: vehicleData  // Include vehicle data
        };
        
        // If elevation data is already available, send a lightweight version
        if (elevationData) {
            console.log('📊 Using pre-fetched elevation data (sending lightweight version)');
            
            // Instead of sending the full grid (can be 10MB+), send only what's needed:
            // 1. Header info (small)
            // 2. Elevation values for route coordinates (already calculated)
            requestBody.elevationData = {
                header: elevationData.header,
                source: elevationData.source,
                resolution_m: elevationData.resolution_m,
                // Don't send the full grid - server will use route coordinates with header
                hasGrid: false
            };
            
            // Add pre-calculated elevation values to route coordinates
            requestBody.route.coordinates = essentialRouteData.coordinates.map(coord => ({
                lat: coord.lat,
                lng: coord.lng,
                elevation: coord.elevation || 0
            }));
            
            console.log('📦 Payload size reduced (header only, no full grid)');
        } else if (bbox) {
            console.log('📊 Server will fetch elevation data for bbox');
            requestBody.bbox = bbox;
        } else {
            throw new Error('Either elevationData or bbox must be provided');
        }
        
        const response = await fetch(`${API_URL}/api/v1/terrain/route-analysis`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-API-Key': process.env.REACT_APP_API_KEY || 'frac_d48567809b669980dc4fd133a106fee041784bac36b00dee6ab92a2326f9aad5'
            },
            body: JSON.stringify(requestBody)
        });
        
        if (!response.ok) {
            let errorMessage = `Route analysis API returned ${response.status}`;
            try {
                const errorData = await response.json();
                errorMessage = errorData.error || errorMessage;
            } catch (e) {
                // Response is not JSON, use default message
            }
            throw new Error(errorMessage);
        }
        
        let result;
        try {
            result = await response.json();
        } catch (parseError) {
            throw new Error('Failed to parse API response. The server may be experiencing issues.');
        }
        
        if (!result.success) {
            throw new Error(result.error || 'Route analysis failed');
        }
        
        // Validate response data structure
        if (!result.data) {
            throw new Error('API returned success but no data');
        }
        
        const { elevationProfile, slopeMetrics, weatherData } = result.data;
        if (!elevationProfile || !slopeMetrics || !weatherData) {
            throw new Error('API response missing required terrain analysis data');
        }
        
        console.log('🔍 [DEBUG] API Response Structure:');
        console.log('   Keys in result.data:', Object.keys(result.data));
        console.log('   Has elevationData:', 'elevationData' in result.data);
        console.log('   elevationData value:', result.data.elevationData);
        console.log('   elevationData type:', typeof result.data.elevationData);
        if (result.data.elevationData) {
            console.log('   elevationData.source:', result.data.elevationData.source);
            console.log('   elevationData.grid size:', result.data.elevationData.grid?.length);
        }
        console.log('   elevationProfile keys:', Object.keys(result.data.elevationProfile || {}));
        console.log('   slopeMetrics keys:', Object.keys(result.data.slopeMetrics || {}));
        console.log('   weatherData keys:', Object.keys(result.data.weatherData || {}));
        
        console.log('✅ Real route analysis complete:', {
            elevation_range: `${Math.round(result.data.elevationProfile.min)}m - ${Math.round(result.data.elevationProfile.max)}m`,
            ascent: `${Math.round(result.data.elevationProfile.ascent)}m`,
            max_slope: `${result.data.slopeMetrics.maxSlopeDegrees.toFixed(1)}°`,
            temperature: `${result.data.weatherData.temperature}°C`,
            source: result.data.source
        });
        
        return result.data;
        
    } catch (error) {
        console.error('❌ Failed to perform real route analysis:', error);
        throw error;
    }
};

/**
 * Transform real analysis data to match RouteAnalysisCard format
 * @param {object} realData - Real analysis data from backend
 * @param {object} route - Original route data
 * @param {string} missionName - Mission name
 * @param {object} vehicleData - Vehicle data (cargo weight, component health)
 * @param {object} missionInputs - Mission input data (startDate, duration, vehicle info)
 * @returns {object} Formatted data for RouteAnalysisCard
 */
export const transformRealDataForDisplay = (realData, route, missionName = 'Unnamed Mission', vehicleData = null, missionInputs = null) => {
    // Validate input data
    if (!realData) {
        throw new Error('❌ Real terrain data is required for display transformation');
    }
    
    if (!route || !route.coordinates) {
        throw new Error('❌ Route data is required for display transformation');
    }
    
    const { elevationProfile, weatherData, slopeMetrics, surfaceMetrics, complexityMetrics, curvatureMetrics, terrainClassification, componentStress, traversability } = realData;
    
    // Extract numeric health - DB may store { health, distanceSinceLastService, ... }
    const extractHealthNum = (val) => {
        if (val === null || val === undefined) return null;
        if (typeof val === 'number' && !isNaN(val)) return val;
        if (typeof val === 'object' && val !== null && 'health' in val && typeof val.health === 'number') return val.health;
        return null;
    };
    
    // Validate critical fields
    if (!elevationProfile) {
        throw new Error('❌ Elevation profile data is missing');
    }
    if (!slopeMetrics) {
        throw new Error('❌ Slope metrics data is missing');
    }
    if (!weatherData) {
        throw new Error('❌ Weather data is missing');
    }
    
    console.log('🔍 [DEBUG] transformRealDataForDisplay START');
    console.log('   elevationProfile:', {
        min: elevationProfile.min,
        max: elevationProfile.max,
        ascent: elevationProfile.ascent,
        descent: elevationProfile.descent
    });
    console.log('   slopeMetrics:', {
        maxSlopeDegrees: slopeMetrics.maxSlopeDegrees,
        avgSlopeDegrees: slopeMetrics.avgSlopeDegrees,
        steepSectionsPercent: slopeMetrics.steepSectionsPercent
    });
    console.log('   weatherData:', {
        temperature: weatherData.temperature,
        humidity: weatherData.humidity,
        windSpeed: weatherData.windSpeed
    });
    
    // Load settings from localStorage
    const savedSettings = localStorage.getItem('fracadapt-settings');
    let averageSpeed = 80; // Default 80 km/h
    let baseFuelConsumption = 12; // Default 12 L/100km
    let unitSystem = 'Metric'; // Default
    
    if (savedSettings) {
        try {
            const settings = JSON.parse(savedSettings);
            if (settings.fleet) {
                averageSpeed = settings.fleet.averageSpeed || 80;
                baseFuelConsumption = settings.fleet.fuelConsumptionRate || 12;
            }
            if (settings.general) {
                unitSystem = settings.general.unitSystem || 'Metric';
            }
        } catch (error) {
            console.warn('Failed to load settings, using defaults');
        }
    }
    
    // Convert units based on settings
    const isMetric = unitSystem === 'Metric';
    const kmToMiles = 0.621371;
    
    // Calculate distance in appropriate unit
    const distanceKm = route.distance / 1000;
    const distance = isMetric ? distanceKm : distanceKm * kmToMiles;
    const distanceFormatted = distance.toFixed(1);
    const distanceUnitLabel = isMetric ? 'km' : 'mi';
    
    // Calculate speed in appropriate unit
    const speedValue = isMetric ? averageSpeed : Math.round(averageSpeed * kmToMiles);
    const speedUnitLabel = isMetric ? 'km/h' : 'mph';
    
    // Calculate duration from distance and fixed speed
    const durationHours = distance / (isMetric ? averageSpeed : speedValue);
    const durationHoursInt = Math.floor(durationHours);
    const durationMinutes = Math.floor((durationHours - durationHoursInt) * 60);
    
    // Calculate fuel estimate based on vehicle weight and terrain
    // Convert cargo weight from lbs to kg for consistent metric calculation
    const cargoWeightKg = vehicleData?.cargoWeight ? vehicleData.cargoWeight * 0.453592 : 0;
    const weightFactor = cargoWeightKg > 0 ? 1 + (cargoWeightKg / 5000) : 1; // +10% per 5,000 kg (~11,000 lbs)
    const terrainFactor = 1 + (complexityMetrics.score / 200); // +50% for max complexity
    const fuelConsumption = baseFuelConsumption * weightFactor * terrainFactor;
    
    // Fuel calculation (always in liters, but based on distance unit)
    const fuelEstimate = isMetric 
        ? ((distanceKm * fuelConsumption) / 100).toFixed(1)
        : ((distance * fuelConsumption * 1.60934) / 100).toFixed(1); // Convert mpg equivalent
    
    // Debug: Check elevation data before returning
    console.log('🔍 [TRANSFORM] Preparing to return analysis:');
    console.log('   realData.elevationData:', realData.elevationData);
    console.log('   Will set elevationData to:', realData.elevationData || null);
    
    return {
        routeIndex: route.id || 0,
        routeType: route.routeType || 'Main Route',
        routeColor: route.color || '#3b82f6',
        stressLevel: complexityMetrics.level === 'High' ? 'high' : complexityMetrics.level === 'Moderate' ? 'medium' : 'low',
        
        // Include elevation data for ML enhancement
        elevationData: realData.elevationData || null,
        
        // Include full componentStress object for ComponentPhysicsCard (Stage 3 & 4)
        componentStress: componentStress,
        
        missionDetails: {
            missionName: missionName,
            startDate: missionInputs?.startDate || 'N/A',
            duration: missionInputs?.duration ? `${missionInputs.duration} days` : 'N/A',
            vehicle: missionInputs?.vehicle || 'N/A',
            vehicleHealth: missionInputs?.vehicleHealth || null,
            cargoCapacity: missionInputs?.cargoCapacity || null,
            additionalCargo: missionInputs?.additionalCargo || 0,
            vehicleMileage: missionInputs?.vehicleMileage || null,
            vehicleYear: missionInputs?.vehicleYear || null,
            totalDistance: `${distanceFormatted} ${distanceUnitLabel}`,
            estimatedTime: `${durationHoursInt}h ${durationMinutes}m`,
            avgSpeed: `${speedValue} ${speedUnitLabel}`, // Fixed speed from settings, converted to appropriate unit
            fuelEstimate: `${fuelEstimate} L`,
            cargoWeight: vehicleData?.cargoWeight 
                ? isMetric 
                    ? `${Math.round(vehicleData.cargoWeight * 0.453592).toLocaleString()} kg` 
                    : `${Math.round(vehicleData.cargoWeight).toLocaleString()} lbs`
                : 'N/A'
        },
        
        terrainParameters: {
            // REAL Environmental data (from weather API or SRTM-derived)
            // Show temperature range if forecast data available, handle null values
            temperature: weatherData.temperature !== null && weatherData.temperatureMin !== undefined && weatherData.temperatureMax !== undefined
                ? (isMetric 
                    ? `${weatherData.temperatureMin}-${weatherData.temperatureMax}°C (avg ${weatherData.temperature}°C)`
                    : `${Math.round(weatherData.temperatureMin * 9/5 + 32)}-${Math.round(weatherData.temperatureMax * 9/5 + 32)}°F (avg ${Math.round(weatherData.temperature * 9/5 + 32)}°F)`)
                : weatherData.temperature !== null
                    ? (isMetric 
                        ? `${weatherData.temperature}°C`
                        : `${Math.round(weatherData.temperature * 9/5 + 32)}°F`)
                    : null,
            moisture: weatherData.moisture || 'Unknown',
            moisturePercent: weatherData.humidity !== null ? `${weatherData.humidity}%` : 'N/A',
            vegetationCover: `${terrainClassification.vegetationCover}%`,
            dustFactor: weatherData.dustFactor !== null ? weatherData.dustFactor : null,
            aspect: terrainClassification.aspect,
            weatherSource: weatherData.source,
            durationDays: weatherData.durationDays || 0,
            dateWarning: weatherData.dateWarning || null,
            isOutOfRange: weatherData.isOutOfRange || false,
            
            // REAL Elevation data from SRTM (always in meters, UI converts based on unit system)
            elevationGain: Math.round(elevationProfile.ascent),
            descent: Math.round(elevationProfile.descent),
            maxElevation: Math.round(elevationProfile.max),
            minElevation: Math.round(elevationProfile.min),
            elevationRange: `${Math.round(elevationProfile.min)}-${Math.round(elevationProfile.max)}`,
            
            // REAL Slope data from SRTM (store both degrees and percent)
            maxSlopeDegrees: slopeMetrics.maxSlopeDegrees,
            maxSlopePercent: Math.round(slopeMetrics.maxSlopePercent),
            avgSlopeDegrees: slopeMetrics.avgSlopeDegrees,
            avgSlopePercent: Math.round(slopeMetrics.avgSlopePercent),
            maxSlope: Math.round(slopeMetrics.maxSlopePercent), // Keep for backward compatibility
            avgSlope: Math.round(slopeMetrics.avgSlopePercent), // Keep for backward compatibility
            slopeRange: `${Math.round(slopeMetrics.avgSlopePercent)}-${Math.round(slopeMetrics.maxSlopePercent)}%`,
            steepSections: Math.round(slopeMetrics.steepSectionsPercent),
            gradeClass: slopeMetrics.gradeClassification,
            
            // REAL Surface conditions from SRTM (convert roughness based on unit system)
            roughnessIndex: Math.round(surfaceMetrics.roughnessIndex),
            surfaceRoughness: isMetric
                ? surfaceMetrics.surfaceRoughness.toFixed(2) + ' m'
                : (surfaceMetrics.surfaceRoughness * 3.28084).toFixed(2) + ' ft',
            // Terrain Curvature: Zevenbergen-Thorne (1987) scientific method from DEM
            // Returns curvatureIndex normalized to 0-100 scale
            terrainCurvature: curvatureMetrics?.curvatureIndex ?? 0,
            terrainCurvatureRaw: curvatureMetrics?.meanCurvature ?? 0, // Raw curvature value for detailed analysis
            // Route Complexity Index: Derived from roughness, retained for backward compatibility
            routeComplexityIndex: Math.round(surfaceMetrics.roughnessIndex * 0.8),
            tractionIndex: Math.round(surfaceMetrics.tractionIndex),
            obstacleDensity: Math.round(surfaceMetrics.obstacleDensity),
            
            // REAL Complexity and traversability
            complexity: {
                level: complexityMetrics.level,
                score: Math.round(complexityMetrics.score),
                color: complexityMetrics.level === 'High' ? 'red' : complexityMetrics.level === 'Moderate' ? 'yellow' : 'green'
            },
            tti: traversability.tti,
            accessibility: traversability.accessibility,
            fractalComplexity: Math.round(complexityMetrics.score * 0.8),
            
            // REAL Component health (from vehicle database or physics-based calculations)
            // Extract numeric health - DB may store { health, distanceSinceLastService, ... }
            suspensionHealth: extractHealthNum(vehicleData?.componentHealth?.suspension) ?? componentStress.suspension.currentHealth,
            brakeHealth: extractHealthNum(vehicleData?.componentHealth?.brakes) ?? componentStress.brakes.currentHealth,
            engineHealth: extractHealthNum(vehicleData?.componentHealth?.engine) ?? componentStress.engine.currentHealth,
            transmissionHealth: extractHealthNum(vehicleData?.componentHealth?.transmission) ?? componentStress.transmission.currentHealth,
            
            suspensionWearRate: componentStress.suspension.wearRate,
            brakeWearRate: componentStress.brakes.wearRate,
            engineLoadStress: componentStress.engine.wearRate,
            transmissionShiftStress: componentStress.transmission.wearRate,
            
            suspensionPostMission: componentStress.suspension.postMissionHealth,
            brakePostMission: componentStress.brakes.postMissionHealth,
            enginePostMission: componentStress.engine.postMissionHealth,
            transmissionPostMission: componentStress.transmission.postMissionHealth,
            
            suspensionNextService: componentStress.suspension.nextService,
            brakeNextService: componentStress.brakes.nextService,
            engineNextService: componentStress.engine.nextService,
            transmissionNextService: componentStress.transmission.nextService,
            
            // ================================================================================
            // PHYSICS-BASED MODEL OUTPUTS (Quarter-Car Dynamics + Miner's Fatigue)
            // For comparison with calibrated linear model - implements FracAdapt paper formulas
            // ================================================================================
            suspensionPhysics: componentStress.suspensionPhysics ? {
                currentHealth: extractHealthNum(componentStress.suspensionPhysics.currentHealth) ?? 90,
                wearRate: componentStress.suspensionPhysics.wearRate,
                postMissionHealth: componentStress.suspensionPhysics.postMissionHealth,
                modelType: componentStress.suspensionPhysics.modelType,
                physicsParams: componentStress.suspensionPhysics.physicsParams,
                references: componentStress.suspensionPhysics.references
            } : null,
            brakePhysics: componentStress.brakePhysics ? {
                currentHealth: extractHealthNum(componentStress.brakePhysics.currentHealth) ?? 85,
                wearRate: componentStress.brakePhysics.wearRate,
                postMissionHealth: componentStress.brakePhysics.postMissionHealth,
                modelType: componentStress.brakePhysics.modelType,
                physicsParams: componentStress.brakePhysics.physicsParams,
                references: componentStress.brakePhysics.references
            } : null,
            enginePhysics: componentStress.enginePhysics ? {
                currentHealth: extractHealthNum(componentStress.enginePhysics.currentHealth) ?? 92,
                wearRate: componentStress.enginePhysics.wearRate,
                postMissionHealth: componentStress.enginePhysics.postMissionHealth,
                modelType: componentStress.enginePhysics.modelType,
                physicsParams: componentStress.enginePhysics.physicsParams,
                references: componentStress.enginePhysics.references
            } : null,
            transmissionPhysics: componentStress.transmissionPhysics ? {
                currentHealth: extractHealthNum(componentStress.transmissionPhysics.currentHealth) ?? 90,
                wearRate: componentStress.transmissionPhysics.wearRate,
                postMissionHealth: componentStress.transmissionPhysics.postMissionHealth,
                modelType: componentStress.transmissionPhysics.modelType,
                physicsParams: componentStress.transmissionPhysics.physicsParams,
                references: componentStress.transmissionPhysics.references
            } : null,
            
            // Terrain classification from SRTM
            terrainClass: terrainClassification.terrainType,
            surfaceType: terrainClassification.surfaceType,
            soilType: terrainClassification.soilType,
            elevationZone: terrainClassification.elevationZone,
            recommendedSpeed: getRecommendedSpeed(complexityMetrics.score, slopeMetrics.maxSlopeDegrees),
            
            // Maintenance impact
            maintenancePriorityScore: Math.round(complexityMetrics.score),
            maintenanceImpact: getMaintenanceImpact(componentStress),
            maintenanceAlerts: generateMaintenanceAlerts(componentStress)
        },
        
        routeComparison: {
            advantages: generateAdvantages(route, complexityMetrics, traversability),
            disadvantages: generateDisadvantages(route, complexityMetrics, traversability)
        }
    };
};

// Helper functions

function getRecommendedSpeed(complexity, maxSlope) {
    if (complexity >= 80 || maxSlope > 30) return '10-20 km/h';
    if (complexity >= 70 || maxSlope > 20) return '15-25 km/h';
    if (complexity >= 50 || maxSlope > 15) return '25-35 km/h';
    if (complexity >= 30 || maxSlope > 10) return '35-45 km/h';
    return '40-60 km/h';
}

function getMaintenanceImpact(componentStress) {
    const avgWear = (
        componentStress.suspension.wearRate +
        componentStress.brakes.wearRate +
        componentStress.engine.wearRate +
        componentStress.transmission.wearRate
    ) / 4;
    
    if (avgWear > 15) return 'High maintenance required - Multiple systems stressed';
    if (avgWear > 10) return 'Moderate maintenance - Regular service recommended';
    if (avgWear > 5) return 'Low maintenance - Normal wear expected';
    return 'Minimal maintenance - Excellent conditions';
}

function generateMaintenanceAlerts(componentStress) {
    const alerts = [];
    
    Object.entries(componentStress).forEach(([component, data]) => {
        if (data.postMissionHealth < 50) {
            alerts.push({
                level: 'critical',
                message: `${component.charAt(0).toUpperCase() + component.slice(1)} replacement recommended before mission`,
                icon: '🔴'
            });
        } else if (data.postMissionHealth < 70) {
            alerts.push({
                level: 'warning',
                message: `${component.charAt(0).toUpperCase() + component.slice(1)} service recommended after mission`,
                icon: '🟡'
            });
        }
    });
    
    if (alerts.length === 0) {
        alerts.push({
            level: 'good',
            message: 'Normal operation expected - No immediate maintenance required',
            icon: '🟢'
        });
    }
    
    return alerts;
}

function generateAdvantages(route, complexity, traversability) {
    const advantages = [];
    
    if (route.id === 0) {
        advantages.push('Shortest distance');
        advantages.push('Primary route recommendation');
    }
    
    if (complexity.score < 40) advantages.push('Low terrain complexity');
    if (traversability.tti > 70) advantages.push('High traversability');
    if (traversability.riskLevel.level === 'Low') advantages.push('Low operational risk');
    
    return advantages;
}

function generateDisadvantages(route, complexity, traversability) {
    const disadvantages = [];
    
    if (route.id > 0) {
        disadvantages.push('Longer distance than main route');
    }
    
    if (complexity.score > 60) disadvantages.push('Higher terrain complexity');
    if (traversability.tti < 50) disadvantages.push('Lower traversability score');
    if (traversability.riskLevel.level === 'High') disadvantages.push('High operational risk');
    
    return disadvantages;
}

